export type PromptAudience = 'user' | 'guest' | 'both';

export const PROMPT_AUDIENCE_OPTIONS: { value: PromptAudience; label: string }[] = [
  { value: 'both', label: 'Users and guests' },
  { value: 'user', label: 'Users only' },
  { value: 'guest', label: 'Guests only' }
];

/**
 * Audience of a Single Prompt. Rows saved before the audience column existed only have inbound.
 */
export function promptAudience(prompt: { audience?: string; inbound?: boolean | number }): PromptAudience {
  if (prompt && (prompt.audience === 'user' || prompt.audience === 'guest' || prompt.audience === 'both')) {
    return prompt.audience;
  }
  return prompt && prompt.inbound == true ? 'guest' : 'user';
}

export function promptAppliesToUsers(prompt: { audience?: string; inbound?: boolean | number }): boolean {
  return promptAudience(prompt) !== 'guest';
}

export function promptAppliesToGuests(prompt: { audience?: string; inbound?: boolean | number }): boolean {
  return promptAudience(prompt) !== 'user';
}

export function promptAudienceLabel(audience: PromptAudience): string {
  if (audience === 'both') {
    return 'Both';
  }
  return audience === 'guest' ? 'Guests' : 'Users';
}

// Keep these in step with api/goodseeker/company/story_prompt_csv.py, so the import preview
// flags exactly the rows the server will skip.
const USER_VALUES = ['user', 'users', 'u', 'user only', 'users only', 'only users', 'member', 'members'];
const GUEST_VALUES = ['guest', 'guests', 'g', 'guest only', 'guests only', 'only guests', 'testimonial'];
const BOTH_VALUES = [
  'both', 'all', 'b', 'everyone',
  'users and guests', 'guests and users', 'users & guests', 'guests & users',
  'users/guests', 'guests/users', 'users, guests', 'guests, users', 'both users and guests'
];
const ACTIVE_VALUES = ['yes', 'y', 'true', '1', 'active', 'on', 'enabled', '✓', '✔'];
const INACTIVE_VALUES = ['no', 'n', 'false', '0', 'inactive', 'off', 'disabled', '✗', '✘'];

function normalizeCell(value: string): string {
  return (value || '').trim().toLowerCase().split(/\s+/).filter(Boolean).join(' ');
}

/** 'user' | 'guest' | 'both', or null when the CSV value isn't recognised. */
export function parsePromptAudience(value: string): PromptAudience | null {
  const normalized = normalizeCell(value);
  if (USER_VALUES.includes(normalized)) {
    return 'user';
  }
  if (GUEST_VALUES.includes(normalized)) {
    return 'guest';
  }
  if (BOTH_VALUES.includes(normalized)) {
    return 'both';
  }
  return null;
}

/** true / false for a recognised CSV Active value, null when it isn't recognised. */
export function parsePromptActive(value: string): boolean | null {
  const normalized = normalizeCell(value);
  if (ACTIVE_VALUES.includes(normalized)) {
    return true;
  }
  if (INACTIVE_VALUES.includes(normalized)) {
    return false;
  }
  return null;
}

/** Same rule as the server: spacing and letter case don't make two prompts different. */
export function promptKey(text: string): string {
  return (text || '').split(/\s+/).filter(Boolean).join(' ').toLowerCase();
}
